l<template>
    <x-container fluid>
        <template v-if="loading">
            <loading-spinner/>
        </template>
        <template v-if="!loading">
            <x-layout class="question-set">
                <x-flex xs2 offset-xs1>
                    <h3 class="question-set-title">Product</h3>
                </x-flex>

                <x-flex xs6 class="question-set-questions">
                    <x-container>
                        <template v-for="(question, key) in filteredProductQuestions">
                            <!-- contract_value -->
                            <x-layout v-if="question.field === 'contract_value'">
                                <x-flex xs6>
                                    <x-currency-text-field
                                        v-model="contractValue"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :prefix="currencyCode"
                                        :rules="getRules(question, contractValue)"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <x-layout v-if="question.type === 'currency' && !(question.field === 'contract_value') && isEditable(isAmendment, question)">
                                <x-flex xs6>
                                    <x-currency-text-field
                                        v-if="isProductDataProperty(question)"
                                        v-model="submission.product_data[question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :prefix="currencyCode"
                                        :rules="getRules(question)"
                                        :data-test="question.field"
                                        outline
                                    />
                                    <x-currency-text-field
                                        v-else
                                        v-model="submission[question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :prefix="currencyCode"
                                        :rules="getRules(question)"
                                        :data-test="question.field"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <!-- delivery_period -->
                            <x-layout v-if="question.type === 'risk_period'">
                                <x-flex xs6>
                                    <x-text-field
                                        v-model="submission.product_data[question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :suffix="submission.product_data[question.field] === 1 ? 'day' : 'days'"
                                        :messages="getRiskPeriodMessage(question)"
                                        :rules="getRules(question)"
                                        outline
                                    />
                                </x-flex>

                                <x-flex xs6 v-if="isQuestionPairedWith(question)">
                                    <x-text-field
                                        v-model="submission.product_data[question.paired_question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.paired_question.label"
                                        placeholder=" "
                                        :suffix="submission.product_data[question.paired_question.field] === 1 ? 'day' : 'days'"
                                        :rules="getRules(question.paired_question)"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <x-layout v-if="question.type === 'text' && isEditable(isAmendment, question)">
                                <x-flex xs6>
                                    <x-text-field
                                        v-if="isProductDataProperty(question)"
                                        v-model="submission.product_data[question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :suffix="getSuffix(question)"
                                        :rules="getRules(question)"
                                        :data-test="question.field"
                                        outline
                                    />
                                    <x-text-field
                                        v-else
                                        v-model="submission[question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :suffix="getSuffix(question)"
                                        :rules="getRules(question)"
                                        :data-test="question.field"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <!-- total_risk_period -->
                            <x-layout v-if="!isDeclaration && question.field === 'total_risk_period'">
                                <x-flex xs6 class="d-flex">
                                    <x-text-field
                                        v-model="totalRiskPeriodYears"
                                        class="risk-period-input required"
                                        :label="question.label"
                                        placeholder=" "
                                        :suffix="totalRiskPeriodYears === 1 ? 'year' : 'years'"
                                        :rules="[
                                            v => ruleRequired(v, 'Total years'),
                                            v => ruleGreaterThanOrEqualTo(v, 0, 'Total years')
                                        ]"
                                        outline
                                    />

                                    <span class="risk-period-divider">/</span>

                                    <x-text-field
                                        v-model="totalRiskPeriodDays"
                                        class="risk-period-input"
                                        label=" "
                                        :suffix="totalRiskPeriodDays === 1 ? 'day' : 'days'"
                                        :rules="[
                                            v => ruleRequired(v, 'Total days'),
                                            v => ruleGreaterThanOrEqualTo(v, 0, 'Total days'),
                                            v => ruleLessThan(v, 365, 'Total days')
                                        ]"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <!-- business_line -->
                            <x-layout v-if="question.field === 'business_line'">
                                <x-flex xs6>
                                    <x-select
                                        v-model="submission.product_data[question.field]"
                                        :items="businessLines"
                                        label="Business line"
                                        placeholder=" "
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        :menu-props="{ offsetY: true }"
                                    />
                                </x-flex>
                            </x-layout>

                            <!-- sub_sector_id -->
                            <x-layout v-if="question.field === 'sub_sector_id'">
                                <x-flex xs6>
                                    <x-select
                                        v-model="selectedSector"
                                        :items="sectors"
                                        class="required"
                                        label="Choose sector"
                                        placeholder="Sector"
                                        item-value="id"
                                        item-text="name"
                                        :rules="[v => ruleRequired(v, 'Sector')]"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        :menu-props="{ offsetY: true }"
                                    />

                                    <x-select
                                        v-if="selectedSector"
                                        v-model="selectedSubSector"
                                        :items="subSectors"
                                        class="required"
                                        label="Choose sub sector"
                                        placeholder="Sub sector"
                                        item-value="id"
                                        item-text="name"
                                        :rules="[v => ruleRequired(v, 'Sub sector')]"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        :menu-props="{ offsetY: true }"
                                    />

                                    <x-text-field
                                        v-if="showCustomSubSectorField"
                                        class="custom-sub-sector required"
                                        v-model="submission.custom_sub_sector"
                                        :rules="[v => ruleRequired(v, 'Sub sector')]"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <!-- waiting_period/requested_waiting_period -->
                            <x-layout v-if="showQuestion(question) && question.type === 'waiting_period'">
                                <x-flex xs6>
                                    <x-select
                                        v-model="submission[question.field]"
                                        :items="filteredWaitingPeriods"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        item-value="value"
                                        item-text="name"
                                        :rules="getRules(question)"
                                        append-icon="keyboard_arrow_down"
                                        :data-test="question.field"
                                        outline
                                        :readonly="filteredWaitingPeriods.length === 1"
                                        :menu-props="{ offsetY: true }"
                                    />
                                </x-flex>
                            </x-layout>

                            <!-- monitoring_provisions -->
                            <x-layout v-if="question.type === 'boolean'">
                                <x-flex xs12>
                                    <x-radio-group
                                        v-model="submission.product_data[question.field]"
                                        :row="true"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :rules="getRules(question)"
                                        :data-test="question.field"
                                    >
                                        <template v-slot:label>
                                            {{ question.label_description ? question.label_description : question.label }}
                                        </template>

                                        <x-radio label="Yes" :value="true" />

                                        <x-radio label="No" :value="false" />
                                    </x-radio-group>
                                </x-flex>
                            </x-layout>

                            <template v-if="question.field === 'prior_experience'">
                                <x-layout v-if="submission.product_data.prior_experience === true && !isStandalonePolicy">
                                    <x-flex xs12>
                                        <div class="info-box">
                                            Please add details of prior experience to additional information on the next step.
                                        </div>
                                    </x-flex>
                                </x-layout>
                            </template>

                            <!-- special_conditions -->
                            <x-layout v-if="question.type === 'textarea'">
                                <x-flex xs12>
                                    <x-textarea
                                        v-model="submission.product_data[question.field]"
                                        :class="{ 'required': getHasRequiredRule(question) }"
                                        :label="question.label"
                                        placeholder=" "
                                        :rules="getRules(question)"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>
                        </template>
                    </x-container>
                </x-flex>
            </x-layout>

            <slot name="submit-row"/>
        </template>
    </x-container>
</template>

<script>
import ValuePercentSplitInput from '~/components/Shared/UIComponents/ValuePercentSplitInput';
import rules from '~/mixins/rules';
import productConfig from '~/mixins/productConfig';
import _ from 'lodash';
import submissionQuoteHelpers from "../../../mixins/submissionQuoteHelpers";

export default {
    name: 'ProductQuestions',
    components: {
        ValuePercentSplitInput,
    },
    mixins: [
        rules,
        productConfig,
        submissionQuoteHelpers
    ],
    props: ['value', 'facilities', 'info', 'commitment', 'isAmendment', 'currentStep'],
    data() {
        return {
            loading: false,
            selectedSector: null,
            selectedSubSector: null,
            showCustomSubSectorField: false,
            sectors: [],
            subSectors: [],
            businessLines: [],
            waitingPeriods: this.$root.waitingPeriods,
            existingLimitOfLiability: 0,
        }
    },
    computed: {
        isDeclaration() {
            return this.submission.risk_type === 'declaration';
        },
        isCommitment() {
            return this.submission?.quote_type?.key === 'commitment';
        },
        isInvoice() {
            return this.submission?.quote_type?.key === 'invoice';
        },
        isStandalonePolicy() {
            return this.submission.risk_structure === 'standalone_policy';
        },
        risk() {
            return this.submission;
        },
        submission: {
            get() {
                return this.value;
            },
            set: function (submission) {
                this.$emit('input', submission);
            }
        },
        filteredWaitingPeriods() {
            if (Boolean(this.selectedFacility) && Boolean(this.selectedFacility.waiting_period_values)) {
                return this.waitingPeriods.filter(wp => this.selectedFacility.waiting_period_values.includes(wp.value));
            }

            return this.waitingPeriods.filter(wp => wp.default);
        },
        contractValue: {
            get() {
                return this.isDeclaration
                    ? this.submission.current_contract_value
                    : this.submission.contract_value
            },
            set(value) {
                if (this.isDeclaration) {
                    this.submission.current_contract_value = value;
                } else {
                    this.submission.contract_value = value;
                }
            }
        },
        commitmentAvailableCapacity() {
            return this.commitment?.available_capacity || this.submission.commitment_declaration.available_capacity;
        },
        remainingCapacityMessage() {
            return [`Remaining capacity is: ${this.$options.filters.currency(this.commitmentAvailableCapacity, this.currencyCode)}`]
        },
        hasSectorQuestion() {
            return this.productQuestions.findIndex(question => question.field === 'sub_sector_id') !== -1;
        },
        filteredProductQuestions() {
            let filteredProductQuestions = _.cloneDeep(this.productQuestions);

            filteredProductQuestions.forEach(question => {
                //if question is paired with another question
                if (question.hasOwnProperty('pair_with')) {
                    const pairedWith = question.pair_with;

                    //add paired question into it
                    question.paired_question = filteredProductQuestions.find(question => question.field === pairedWith);

                    //remove paired question from main questions list inorder to avoid duplication
                    filteredProductQuestions.splice(filteredProductQuestions.findIndex(question => question.field === pairedWith), 1);
                }
            });

            if (this.isStandalonePolicy) {
                filteredProductQuestions = filteredProductQuestions.filter(question => question?.field !== 'prior_experience');
                this.submission.product_data.prior_experience = false;
            }

            return filteredProductQuestions;
        },
    },
    watch: {
        value: {
            handler() {
                this.$emit('input', this.value);
            },
            deep: true
        },
        selectedSector(newValue) {
            this.subSectors = this.sectors.find(sector => (sector.id === newValue)).sub_sector;

            if (this.subSectors.length === 1) {
                this.selectedSubSector = this.subSectors[0].id;
            }
        },
        selectedSubSector(newValue) {
            this.showCustomSubSectorField = false;
            this.submission.sub_sector_id = newValue;
            if (this.subSectors.find(subSectors => (subSectors.id === newValue && subSectors.name === 'Other'))) {
                this.showCustomSubSectorField = true;
            }
        },
        filteredWaitingPeriods: {
            handler(newValue) {
                if (newValue.length === 1 && !this.isDeclaration) {
                    this.submission.requested_waiting_period = newValue[0].value;
                }
            },
            immediate: true
        },
    },
    created() {
        this.setProductDataProperties();

        if (this.isAmendment && this.isCommitment) {
            this.existingLimitOfLiability = this.submission.limit_of_liability;
        }
    },
    mounted() {
        axios.get(this.$root.api(`sectors`)).then(res => {
            this.sectors = res.data.data;
            this.setSectorData();
        });
        axios.get(this.$root.api(`business-lines`)).then(res => {
            this.businessLines = res.data.data;
        });

        // reset form validation so above changes do not trigger validation errors to show
        this.$emit('form:reset-validation');
    },
    methods: {
        showQuestion(question) {
            if (!question.hasOwnProperty('rules') || (question.hasOwnProperty('rules') && question.rules.length < 1)) {
                return true;
            }

            //looking for required if rule
            const requiredIf = this.searchAndGetRule(question, 'required_if:');

            if (!requiredIf) {
                return true;
            }

            const firstSplit = requiredIf.split(':');
            if (firstSplit.length < 1) {
                return true;
            }

            const secondSplit = firstSplit[1].split(',');
            if (secondSplit.length < 1 || secondSplit[0] === ' ') {
                return true;
            }

            return this.submission[secondSplit[0]] === secondSplit[1];
        },
        isQuestionPairedWith(question) {
            if (!(question.hasOwnProperty('pair_with') && question.pair_with) || !(question.hasOwnProperty('paired_question') && question.paired_question)) {
                return false;
            }

            return question.paired_question.field === question.pair_with;
        },
        getRules(question, value) {
            let questionRules = [];

            if (!value) {
                value = this.isProductDataProperty(question) ? this.submission.product_data[question.field] : this.submission[question.field];
            }

            if (!question.hasOwnProperty('rules') || (question.hasOwnProperty('rules') && question.rules.length < 1)) {
                return [];
            }

            const requiredIfRegex = /^required_if:\w+,\w+/g;
            question.rules.forEach(rule => {
                if (rule.match(requiredIfRegex)) {
                    let ruleString = rule.replace('required_if:', '');
                    let requiredValues = ruleString.split(",");
                    if (this.submission.hasOwnProperty(requiredValues[0]) && this.submission[requiredValues[0]] == requiredValues[1]) {
                        questionRules.push(
                            this.ruleRequired(value, question.label)
                        );
                    }
                }
            });

            //required
            if ('required' in question.rules || question.rules.includes('required')) {
                questionRules.push(
                    this.ruleRequired(value, question.label)
                );
            }

            //numeric
            if ('numeric' in question.rules || question.rules.includes('numeric')) {
                questionRules.push(
                    this.ruleNumeric(value, question.label)
                );
            }

            //min
            const minRule = this.searchAndGetRule(question, 'min');
            if ('min' in question.rules || minRule) {
                const minRuleArray = minRule.split(':');
                questionRules.push(
                    this.ruleGreaterThanOrEqualTo(value, minRuleArray[1], question.label)
                );
            }

            //gt => greater than
            const gtRule = this.searchAndGetRule(question, 'gt');
            if ('gt' in question.rules || gtRule) {
                const gtRuleArray = gtRule.split(':');
                questionRules.push(
                    this.ruleGreaterThan(value, gtRuleArray[1], question.label)
                );
            }

            //max and lte => less than or equal to
            let maxRule = this.searchAndGetRule(question, 'max');
            if (!maxRule) {
                maxRule = this.searchAndGetRule(question, 'lte');
            }
            if ('max' in question.rules || 'lte' in question.rules || maxRule) {
                const maxRuleArray = maxRule.split(':');
                let maximum = 0;

                if (maxRuleArray[1] === 'product_data.total_contract_value') {
                    maximum = this.submission.product_data.total_contract_value;
                } else if (maxRuleArray[1] === 'contract_value') {
                    maximum = this.contractValue;
                } else {
                    maximum = maxRuleArray[1];
                }

                questionRules.push(
                    this.ruleLessThanOrEqualTo(value, maximum, question.label)
                );
            }

            //if it is limit_of_liability and the product is commitment/invoice than add this additional rule
            if ((this.isCommitment || this.isInvoice) && question.field === 'limit_of_liability') {
                questionRules.push(
                    this.availableCapacityRule(this.submission.product_data.limit_of_liability)
                );
            }

            return questionRules;
        },
        getHasRequiredRule(question) {
            return question.rules.some(rule => rule.startsWith('required'));
        },
        searchAndGetRule(question, searchRule) {
            return question.rules.find(rule => {
                return rule.includes(searchRule);
            });
        },
        getSuffix(question) {
            if (question.hasOwnProperty('suffix')) {
                return question.suffix;
            }

            return '';
        },
        getRiskPeriodMessage(question) {
            if (this.isInvoice && question.field === 'payment_terms') {
                const commitmentPaymentTerms = Number(this.commitment?.product_data.payment_terms || this.submission.commitment_declaration.product_data.payment_terms);
                const invoicePaymentTerms = Number(this.submission.product_data.payment_terms);

                if (invoicePaymentTerms && invoicePaymentTerms > commitmentPaymentTerms) {
                    return [`Payment terms has been exceeded, max payment term allowed is ${commitmentPaymentTerms} days`];
                }
            }

            return [];
        },
        setSectorData() {
            if (this.submission.sub_sector_id && this.submission.sub_sector) {
                this.selectedSector = this.submission.sub_sector.sector_id;
                this.selectedSubSector = this.submission.sub_sector_id;
            }
        },
        setProductDataProperties() {
            // does not take into account change of product, will need revisiting when multiple products are added
            if (this.submission.id === -1 && !this.submission.is_copy) {
                // here we want to set the properties for this product on the submission's product_data, so that they are reactive for form validation
                // this.$set is used here so that computed and watch selectedCommodityType become reactive and behave as expected

                this.productQuestions.forEach(question => {
                    const setValue = question.type === 'boolean' ? null : ''
                    if (this.isProductDataProperty(question)) {
                        this.$set(this.submission.product_data, question.field, setValue);
                    }
                })
            }
        },
        availableCapacityRule(v) {
            if (this.isCommitment) {
                return (!this.isAmendment || v >= this.existingLimitOfLiability - this.submission.available_capacity) || 'Existing invoices exceed this amount of capacity.';
            } else if (this.isInvoice) {
                return v <= this.commitmentAvailableCapacity || 'Remaining capacity has been exceeded, amend the entry to proceed.';
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.risk-period-input {
    width: 50%;
}

.risk-period-divider {
    width: 23px;
    margin: 20px 0 0;
    color: $color-body-text;
    line-height: 40px;
    text-align: center;
}

.custom-sub-sector.x-input.x-text-field.x-text-field--outline ::v-deep .x-input__control .x-input__slot {
    margin-top: 0;
}
</style>
